import { api } from '../../../api/client';
import { ICM_LIST_TAKE } from '../icmPaging';
import { PREVIEW_AS_TOKEN, openReportPreview } from '../../../utils/reportPreview';

/**
 * Inventory Management → Tracking — shared client for the five tracking
 * grids (Item Requisition Tracking, Item Process Tracking, Item Transaction
 * History, Cancelled Requisition, LT# COA Tracking).
 *
 * Division scope is NOT a parameter here: the shared axios client sends
 * `X-Division-Id` on every request and the API resolves it through
 * `IDivisionContext` (§18.3) — same pattern as `icmAlertApi.ts`.
 */

/**
 * Server read cap (mirrors `IcmTrackingReader.ClampTake`). The grids fetch one
 * page of this size and page client-side.
 *
 * This is the ONE shared ICM ceiling, not a second number: it was a bespoke
 * 2,000 until 2026-10-05, which silently truncated Item Transaction History
 * (5,756 lots) and LT# COA Tracking (5,654) — the page header showed the true
 * total while the grid footer showed 2,000, and the missing rows could not be
 * reached by paging, sorting or filtering because they were never fetched.
 * See `../icmPaging` for why a large ceiling is safe here.
 */
export const TRACKING_PAGE_CAP = ICM_LIST_TAKE;

export interface TrackingEnvelope<T> {
  rows: T[];
  /** True total behind the criteria — may exceed `rows.length` at the cap. */
  total: number;
}

export interface RequisitionTrackingRow {
  oid: string;
  rqid: string | null;
  itemName: string | null;
  itemCode: string | null;
  specification: string | null;
  catalog: string | null;
  vendorName: string | null;
  orderQty: number;
  stockQty: number;
  packUnit: string | null;
  deliveryPriorityName: string | null;
  departmentName: string | null;
  requestedDate: string | null;
  requestedByName: string | null;
  status: number;
  statusLabel: string | null;
}

export interface ProcessTrackingRow {
  oid: string;
  rqid: string | null;
  itemName: string | null;
  itemCode: string | null;
  specification: string | null;
  catalog: string | null;
  vendorName: string | null;
  receiveId: string | null;
  purchaseOrderNo: string | null;
  lt: string | null;
  vendorLt: string | null;
  expiryDate: string | null;
  packUnit: string | null;
  storageName: string | null;
  departmentName: string | null;
  requestedDate: string | null;
  requestedByName: string | null;
  reviewedDate: string | null;
  reviewedByName: string | null;
  purchaseDate: string | null;
  purchaseByName: string | null;
  receiveDate: string | null;
  receivedByName: string | null;
  distributionDate: string | null;
  givenByName: string | null;
  status: number;
  statusLabel: string | null;
}

export interface CancelledRequisitionRow {
  oid: string;
  rqid: string | null;
  itemName: string | null;
  itemCode: string | null;
  specification: string | null;
  catalog: string | null;
  vendorName: string | null;
  orderQty: number;
  packUnit: string | null;
  deliveryPriorityName: string | null;
  departmentName: string | null;
  requestedDate: string | null;
  requestedByName: string | null;
  canceledDate: string | null;
  canceledByName: string | null;
  rollbackReason: string | null;
}

export interface TransactionHistoryRow {
  oid: string;
  lt: string | null;
  vendorLt: string | null;
  itemName: string | null;
  itemCode: string | null;
  specification: string | null;
  catalog: string | null;
  vendorName: string | null;
  categoryName: string | null;
  gradeName: string | null;
  /** The REQUISITION's department (legacy `RQID.department`), not the lot's. */
  departmentName: string | null;
  requestedDate: string | null;
  requestedByName: string | null;
  reviewedDate: string | null;
  reviewedByName: string | null;
  purchaseDate: string | null;
  purchaseByName: string | null;
  rqidText: string | null;
  purchaseOrderNo: string | null;
  receiveId: string | null;
  receiveDate: string | null;
  receivedByName: string | null;
  distributionDate: string | null;
  /** `Distribution.DistributedBy` — the legacy column; `GivenBy` is hidden there. */
  distributedByName: string | null;
  consumptionDate: string | null;
  consumptionByName: string | null;
  disposedDate: string | null;
  disposedByName: string | null;
  expiryDate: string | null;
  packUnit: string | null;
  unitPrice: number | null;
  status: number;
  statusLabel: string | null;
}

export interface LtCoaTrackingRow {
  oid: string;
  lt: string | null;
  vendorLt: string | null;
  itemName: string | null;
  itemCode: string | null;
  vendorName: string | null;
  catalog: string | null;
  storageName: string | null;
  departmentName: string | null;
  requestedDate: string | null;
  distributionDate: string | null;
  expiryDate: string | null;
  status: number;
  statusLabel: string | null;
  vendorReagentCertificate: string | null;
  hasCoaAttachment: boolean;
}

type Envelope<T> = { data?: T[]; meta?: { total?: number } };

async function get<T>(path: string, params?: Record<string, unknown>): Promise<TrackingEnvelope<T>> {
  const res = await api.get(path, { params: { take: TRACKING_PAGE_CAP, ...params } });
  const body = res.data as Envelope<T>;
  const rows = body.data ?? [];
  return { rows, total: body.meta?.total ?? rows.length };
}

export const fetchRequisitionTracking = (search?: string) =>
  get<RequisitionTrackingRow>('/api/v1/icm-tracking/requisition', { search });

export const fetchProcessTracking = (search?: string) =>
  get<ProcessTrackingRow>('/api/v1/icm-tracking/process', { search });

/** `months` is the ICM date window legacy applies here invisibly through
 *  RequisitionViewcontroller (3M by default); undefined = ALL. */
export const fetchCancelledRequisition = (search?: string, months?: number | null) =>
  get<CancelledRequisitionRow>('/api/v1/icm-tracking/cancelled-requisition',
    { search, months: months ?? undefined });

/** LT# descending (owner request 2026-11-02): the largest/newest lot numbers
 *  on top and the unassigned "0" lots at the bottom. This matches the XAF
 *  model's own default order (Descending); the legacy
 *  `ICMLTReportController.OnActivated` ascending override is not reproduced.
 *  The SP sorts LT NUMERICALLY, so it stays correct across digit boundaries. */
export const fetchTransactionHistory = (search?: string) =>
  get<TransactionHistoryRow>('/api/v1/icm-tracking/transaction-history', { search, sortDesc: true });

export const fetchLtCoaTracking = (search?: string) =>
  get<LtCoaTrackingRow>('/api/v1/icm-tracking/lt-coa', { search });

/** Pulls the filename out of a `Content-Disposition: attachment;
 *  filename="…"` response header (falls back to the quoted-less form too). */
function fileNameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) { try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ } }
  const quoted = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return quoted?.[1]?.trim() ?? null;
}

/** Streams a blob response through an authenticated `<a download>` click — a
 *  plain `<a href>` or `window.open` would 401 -> /login, because the JWT
 *  lives in an Authorization header, not a cookie (same pattern as
 *  `analysis/rawDataPreview.ts`'s `downloadRawDataFile`).
 *
 *  The saved filename comes from the server's `Content-Disposition` header
 *  when present (e.g. Label Report's `yyyyMMddHHmmss.pdf` — legacy
 *  `ICMLTReportController.LTReportAction_Execute` names the download from a
 *  timestamp computed server-side at generation, not a client-side guess);
 *  `fallbackFileName` only covers an endpoint that omits the header. */
async function downloadBlob(path: string, params: Record<string, unknown> | undefined, fallbackFileName: string): Promise<void> {
  const res = await api.get<Blob>(path, { params, responseType: 'blob' });
  await saveBlob(res, fallbackFileName);
}

/** Same as {@link downloadBlob} but POSTs a JSON body — used where the request
 *  carries a list too long for a URL (see `downloadLabelReport`). */

async function postDownloadBlob(path: string, body: unknown, fallbackFileName: string): Promise<void> {
  const res = await api.post<Blob>(path, body, { responseType: 'blob' });
  await saveBlob(res, fallbackFileName);
}

async function saveBlob(
  // `headers` is widened deliberately: axios types it as AxiosResponseHeaders,
  // whose values may be null, so a Record<string, string | undefined> would not
  // accept a real response.
  res: { data: Blob; headers: unknown },
  fallbackFileName: string,
): Promise<void> {
  const cd = (res.headers as Record<string, unknown> | undefined)?.['content-disposition'];
  const fileName =
    fileNameFromContentDisposition(typeof cd === 'string' ? cd : undefined) ?? fallbackFileName;
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Downloads a lot's COA PDF. Throws (404) when no file is attached — callers
 *  should only invoke this when the row's `hasCoaAttachment` is true. */
export const downloadCoaFile = (distributionId: string, lt: string | null) =>
  downloadBlob(`/api/v1/icm-tracking/lt-coa/${distributionId}/coa-file`, undefined, `${lt ?? distributionId}-COA.pdf`);

/** Downloads the "Label Report" PDF (QR + Item Name + LT No + Dist Date +
 *  Exp Date + Storage) for one or more lots — one page per lot. Shared by
 *  Item Transaction History's toolbar "Label Report" (multi-select) and LT#
 *  COA Tracking's inline "Label" button (always a single id). */
/** POSTs the ids rather than putting them in the query string: a full-page
 *  selection used to overflow the URL and fail with 414 before reaching the
 *  server (1,500 lots worked, 1,700 did not — measured 2026-09-29), which the
 *  user saw only as "Failed to generate the label report". */
export const downloadLabelReport = (distributionIds: string[]) =>
  postDownloadBlob('/api/v1/icm-tracking/lt-coa/label-report', { distributionIds }, 'LabelReport.pdf');

/** The same label report, OPENED in a new tab (legacy's preview behaviour).
 *  Used by Item Initialization's QRLabel cell; LT# COA Tracking's own Label
 *  button still downloads, which is what that screen's bulk action means.
 *
 *  Goes through the SHARED preview-ticket delivery Custom Reporting uses
 *  (`?deliver=token` → `openReportPreview`) rather than a `blob:` URL. The blob
 *  broke the PDF viewer's own Download button: the viewer re-fetches its URL,
 *  and by then the blob was revoked — or the SPA had navigated, which
 *  auto-revokes every object URL the document owns — so the download failed with
 *  a network error and was named after the blob's GUID, a `blob:` URL carrying
 *  no Content-Disposition. The token URL is a real endpoint that stays fetchable
 *  for the store's TTL and serves a proper filename. */
export async function openLabelReport(distributionIds: string[]): Promise<boolean> {
  const res = await api.post('/api/v1/icm-tracking/lt-coa/label-report',
                             { distributionIds }, { params: PREVIEW_AS_TOKEN });
  return openReportPreview(res.data);
}
